import { useMemo, useState } from "react";
import type { MatchSummary } from "@/lib/types";
import { useMatches } from "@/features/matches/useMatches";
import {
  useCoachSummariesList,
  useMatchSummaryMutations,
} from "./hooks/useCoachSummariesList";
import { MatchSummaryComposerModal } from "./components/MatchSummaryComposerModal";
import { PageHeader } from "./components/PageHeader";
import { ConfirmDialog } from "@/components/ConfirmDialog";

/**
 * Coach > Match Summaries list page (M6a).
 *
 * Ports the legacy `renderCoachMatchSummaries`
 * (`js/coaching/match-summaries.js`): the team's match summaries,
 * each with the match label, visibility + linked-evidence counts,
 * a short recap preview, and per-row Edit / Delete.
 *
 * Like Playlists, the **composer is its own modal**
 * (`<MatchSummaryComposerModal>`) — there's no Coach Review
 * surface for summaries. "+ New summary" opens it in create mode.
 */

export function MatchSummariesPage() {
  const summariesQuery = useCoachSummariesList();
  const matchesQuery = useMatches();
  const mutations = useMatchSummaryMutations();

  const summaries = summariesQuery.data?.summaries ?? [];

  const matchLabelById = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of matchesQuery.data ?? []) {
      const teams = `${m.home_team} vs ${m.away_team}`;
      map.set(m.id, m.date ? `${teams} · ${m.date}` : teams);
    }
    return map;
  }, [matchesQuery.data]);

  const [deleting, setDeleting] = useState<number | null>(null);
  // `composer`: null = closed; { summary: null } = create;
  // { summary } = edit.
  const [composer, setComposer] = useState<
    { summary: MatchSummary | null } | null
  >(null);
  const [confirmDelete, setConfirmDelete] = useState<MatchSummary | null>(null);

  const handleDelete = (summary: MatchSummary) => {
    setConfirmDelete(summary);
  };

  const confirmDeleteSummary = async () => {
    if (!confirmDelete) return;
    const summary = confirmDelete;
    setConfirmDelete(null);
    setDeleting(summary.id);
    try {
      await mutations.deleteSummary(summary.id);
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="flex flex-col gap-5" data-testid="summaries-page">
      {confirmDelete ? (
        <ConfirmDialog
          title="Delete summary?"
          message={`Delete this match summary? This can't be undone.`}
          confirmLabel="Delete"
          danger
          onConfirm={confirmDeleteSummary}
          onClose={() => setConfirmDelete(null)}
          data-testid="summaries-delete-confirm"
        />
      ) : null}
      <PageHeader
        title="Match Coaching Summaries"
        description="Publish match-level positives, improvements, and training focus for the team. Private summaries stay coach-only."
        actions={
          <button
            type="button"
            onClick={() => setComposer({ summary: null })}
            data-testid="summaries-new-summary"
            className="bg-accent hover:bg-accent-hover text-white rounded px-4 py-1.5 text-sm font-display uppercase tracking-wide transition-colors"
          >
            + New summary
          </button>
        }
      />

      {summariesQuery.isLoading ? (
        <p
          className="text-ink-muted text-sm font-mono px-3 py-6"
          data-testid="summaries-loading"
        >
          Loading summaries…
        </p>
      ) : summaries.length === 0 ? (
        <p
          className="text-ink-muted text-sm px-3 py-6"
          data-testid="summaries-empty"
        >
          No match summaries yet. Add a team-visible recap after a match.
        </p>
      ) : (
        <ul className="flex flex-col gap-2" data-testid="summaries-list">
          {summaries.map((summary) => (
            <SummaryRow
              key={summary.id}
              summary={summary}
              matchLabel={
                matchLabelById.get(summary.match_id) ?? summary.match_id
              }
              deleting={deleting === summary.id}
              onEdit={() => setComposer({ summary })}
              onDelete={() => handleDelete(summary)}
            />
          ))}
        </ul>
      )}

      {composer ? (
        <MatchSummaryComposerModal
          summary={composer.summary}
          mutations={mutations}
          onClose={() => setComposer(null)}
        />
      ) : null}
    </div>
  );
}

type SummaryRowProps = {
  summary: MatchSummary;
  matchLabel: string;
  deleting: boolean;
  onEdit: () => void;
  onDelete: () => void;
};

function SummaryRow({
  summary,
  matchLabel,
  deleting,
  onEdit,
  onDelete,
}: SummaryRowProps) {
  // Linked-evidence count line — `{n} notes · {n} clips · {n} playlists`.
  const linkedBits: string[] = [];
  const noteCount = summary.note_ids?.length ?? 0;
  const clipCount = summary.clip_ids?.length ?? 0;
  const playlistCount = summary.playlist_ids?.length ?? 0;
  if (noteCount) linkedBits.push(`${noteCount} note${noteCount === 1 ? "" : "s"}`);
  if (clipCount) linkedBits.push(`${clipCount} clip${clipCount === 1 ? "" : "s"}`);
  if (playlistCount) {
    linkedBits.push(
      `${playlistCount} playlist${playlistCount === 1 ? "" : "s"}`,
    );
  }
  const metaParts = [summary.visibility, ...linkedBits];

  const phases: Array<{ label: string; tone: string; text: string }> = [
    {
      label: "Positives",
      tone: "border-good/40 bg-good/10 text-good",
      text: (summary.team_positives ?? "").trim(),
    },
    {
      label: "Improvements",
      tone: "border-orange-400/40 bg-orange-400/10 text-orange-300",
      text: (summary.team_improvements ?? "").trim(),
    },
    {
      label: "Training Focus",
      tone: "border-accent/40 bg-accent/10 text-accent",
      text: (summary.training_focus ?? "").trim(),
    },
  ];
  const bodyFallback = (summary.body ?? "").trim();

  return (
    <li
      className="flex flex-col gap-2 rounded-lg border border-rule bg-bg-card px-3 py-2"
      data-testid="summary-row"
      data-summary-id={summary.id}
    >
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-display text-ink truncate">
            {matchLabel}
          </div>
          <div className="text-ink-muted text-xs font-mono mt-0.5 truncate">
            {metaParts.join(" · ")}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <RowButton
            label="Edit"
            testid="summary-action-edit"
            onClick={onEdit}
          />
          <RowButton
            label="Delete"
            testid="summary-action-delete"
            onClick={onDelete}
            disabled={deleting}
            danger
          />
        </div>
      </div>
      {phases.some((p) => p.text) ? (
        <div
          data-testid="summary-row-phases"
          className="grid grid-cols-1 sm:grid-cols-3 gap-2"
        >
          {phases.map((p) =>
            p.text ? (
              <div
                key={p.label}
                className={`rounded border ${p.tone} px-2 py-1.5 text-xs`}
                data-testid={`summary-row-phase-${p.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="font-display uppercase tracking-wider text-[0.55rem] opacity-80 mb-0.5">
                  {p.label}
                </div>
                <div className="text-ink line-clamp-3 whitespace-pre-line">
                  {p.text}
                </div>
              </div>
            ) : null,
          )}
        </div>
      ) : bodyFallback ? (
        <div className="text-ink-muted text-xs whitespace-pre-line line-clamp-2">
          {bodyFallback}
        </div>
      ) : null}
    </li>
  );
}

function RowButton(props: {
  label: string;
  testid: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={props.onClick}
      disabled={props.disabled}
      data-testid={props.testid}
      title={props.label}
      aria-label={props.label}
      className={[
        "rounded border px-2 py-1 text-xs transition-colors disabled:opacity-40",
        props.danger
          ? "border-rule text-ink-muted hover:border-destructive hover:text-destructive"
          : "border-rule text-ink-muted hover:text-ink hover:bg-surface",
      ].join(" ")}
    >
      {props.label}
    </button>
  );
}
